import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import ManualChip from '../../reportCards/ManualChip';
import { formatDealOptionName, formatMoney } from '../../shared/checklistUtils';

import type { DealOptionsInclusionsChecklistRow } from './types';

interface Props {
  approved: boolean;
  currency: string;
  inc: DealOptionsInclusionsChecklistRow;
  manuallyApproved: boolean;
  roomName?: string;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export const INCLUSION_ROW_GRID_TEMPLATE_COLUMNS = 'minmax(200px, 1fr) 70px 90px 90px 120px 120px 120px 56px';

export default function InclusionRow({
  approved,
  currency,
  inc,
  manuallyApproved,
  roomName,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const hasDescription = !!inc.description;
  const hasRrpOccupancyMismatch = inc.rrpOccupancyMismatchWith.length > 0;
  const hasDetails = hasDescription || !!roomName || hasRrpOccupancyMismatch;
  const needsAction = !approved;

  return (
    // Designer-specified "canon" status treatment (echoed in Margin/Capacity + ApprovalStatusChip), not yet in the theme.
    <Box
      sx={{
        borderLeft: 4,
        borderLeftColor: needsAction ? 'warning.main' : 'success.main',
        borderTop: 1,
        borderTopColor: 'divider',
        bgcolor: (theme) => alpha(needsAction ? theme.palette.warning.light : theme.palette.success.light, 0.06),
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          columnGap: 2,
          display: 'grid',
          gridTemplateColumns: INCLUSION_ROW_GRID_TEMPLATE_COLUMNS,
          px: 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            color="inherit"
            disabled={!hasDetails}
            size="small"
            onClick={() => setDescriptionOpen((current) => !current)}
            endIcon={
              hasDetails ? (
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: descriptionOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                />
              ) : undefined
            }
            sx={{
              justifyContent: 'flex-start',
              minWidth: 0,
              p: 0,
              textAlign: 'left',
              textTransform: 'none',
              '&.Mui-disabled': { color: 'text.primary' },
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'break-word', whiteSpace: 'normal' }}>
              {inc.name || '—'}
            </Typography>
          </Button>
          {manuallyApproved && <ManualChip />}
        </Stack>
        <Typography variant="body2">{inc.type || '—'}</Typography>
        <Typography variant="body2">{formatMoney(inc.dealOptionInclusionRrp, currency)}</Typography>
        <Typography variant="body2">{formatMoney(inc.inclusionAssetValue, currency)}</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            component="span"
            sx={{
              bgcolor: inc.rrpMatch ? 'success.main' : 'warning.main',
              borderRadius: '50%',
              flexShrink: 0,
              height: 9,
              width: 9,
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {inc.rrpMatch ? 'Match' : 'Mismatch'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            component="span"
            sx={{
              bgcolor: inc.rrpOccupancyMismatchWith.length === 0 ? 'success.main' : 'warning.main',
              borderRadius: '50%',
              flexShrink: 0,
              height: 9,
              width: 9,
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {inc.rrpOccupancyMismatchWith.length === 0 ? 'OK' : 'Not scaled'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <ApprovalActionButton
            approved={approved}
            hidden={inc.status === 'blocked'}
            onApprove={() => onApprovalChange(inc.id)}
            onUnapprove={() => onUnapprovalChange(inc.id)}
          />
        </Stack>
      </Box>
      {hasDetails && (
        <Collapse in={descriptionOpen}>
          <Stack
            spacing={1.5}
            sx={{
              mx: 2,
              mb: 2,
              p: 1.5,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {roomName && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.06em', display: 'block', mb: 0.5, textTransform: 'uppercase' }}
                >
                  Room name
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {roomName}
                </Typography>
              </Box>
            )}
            {hasRrpOccupancyMismatch && (
              <Box
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.warning.light, 0.12),
                  border: 1,
                  borderColor: 'warning.main',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  This inclusion has the same RRP in the following deal options, despite having a different number of
                  guests:
                </Typography>
                {inc.rrpOccupancyMismatchWith.map((otherDealOptionName) => (
                  <Typography key={otherDealOptionName} variant="body2" color="text.secondary">
                    {formatDealOptionName(otherDealOptionName)}
                  </Typography>
                ))}
              </Box>
            )}
            {hasDescription && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.06em', display: 'block', mb: 0.5, textTransform: 'uppercase' }}
                >
                  Inclusion description
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {inc.description}
                </Typography>
              </Box>
            )}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
}
